import type { OriginModule } from './originModule';

/**
 * Decision types — first-class decision objects.
 *
 * Decisions can be:
 *  - 'manual'  — explicitly created by a user to record a significant choice
 *  - 'auto'    — system-generated from audit events (e.g. cycle_published, ranking_adjusted)
 *
 * Both kinds appear in the Decisions page table and the Provenance feed.
 */

// ============================================================================
// Related object attachment
// ============================================================================

/**
 * Object kinds a decision can reference. The first four can be attached by
 * hand; the planning kinds are written by the database when a planning act
 * records its own Decision (`authorize_por`, migration 788;
 * `activate_plan_cycle`, migration 797).
 */
export type DecisionRelatedObjectKind =
  | 'item'
  | 'session'
  | 'cycle'
  | 'track'
  | 'plan_cycle'
  | 'plan_version'
  | 'plan_of_record'
  | 'plan_of_record_snapshot';

/** The kinds a person can attach to a decision they write. */
export const ATTACHABLE_DECISION_RELATED_OBJECT_KINDS = ['item', 'session', 'cycle', 'track'] as const;

export const DECISION_RELATED_OBJECT_KIND_LABELS: Record<DecisionRelatedObjectKind, string> = {
  item:    'Item',
  session: 'Session',
  cycle:   'Cycle',
  track:   'Track',
  plan_cycle:              'Plan',
  plan_version:            'Plan version',
  plan_of_record:          'Plan of Record',
  plan_of_record_snapshot: 'Plan of Record snapshot',
};

/** One entry in the related_objects JSONB array stored on the decisions table. */
export interface DecisionRelatedObject {
  kind: DecisionRelatedObjectKind;
  id: string;
}

/**
 * Where a related object opens, or null when it has no page of its own (a plan
 * version or snapshot is reached through its plan) or the kind is one this
 * client does not know — `related_objects` is written by the database too, so
 * a reader must not assume the set is closed.
 */
export function decisionRelatedObjectHref(ref: { kind: string; id: string }): string | null {
  switch (ref.kind) {
    case 'item':       return `/prioritization/items/${ref.id}`;
    case 'session':    return `/prioritization/sessions/${ref.id}`;
    case 'cycle':      return `/prioritization/cycles/${ref.id}`;
    case 'track':      return `/prioritization/tracks/${ref.id}`;
    case 'plan_cycle': return `/planning/plans/${ref.id}`;
    case 'item_pool':  return `/prioritization/lists/${ref.id}`;
    default:           return null;
  }
}

// ============================================================================
// Enums / union types
// ============================================================================

export type DecisionKind = 'manual' | 'auto';

export type DecisionImpact = 'high' | 'medium' | 'low';

export type DecisionStatus = 'active' | 'superseded' | 'reversed';

export type DecisionHealthStatus = 'fresh' | 'aging' | 'stale' | 'orphaned';

/**
 * Structural classification per registry-decision-classes.yaml.
 * Matches the canonical `id` values from that registry.
 */
export type DecisionClass =
  | 'selection'
  | 'range_selection'
  | 'convergence'
  | 'force_ranking'
  | 'review';

export const DECISION_CLASS_LABELS: Record<DecisionClass, string> = {
  selection:       'Selection',
  range_selection: 'Range Selection',
  convergence:     'Convergence',
  force_ranking:   'Force Ranking',
  review:          'Review',
};

export const DECISION_CLASS_COLORS: Record<DecisionClass, string> = {
  force_ranking:   'bg-purple-100 text-purple-700',
  selection:       'bg-teal-100 text-teal-700',
  convergence:     'bg-sky-100 text-sky-700',
  range_selection: 'bg-amber-100 text-amber-700',
  review:          'bg-slate-100 text-slate-600',
};

export const DECISION_CLASS_OPTIONS: { value: DecisionClass; label: string }[] = [
  { value: 'force_ranking',   label: 'Force Ranking' },
  { value: 'selection',       label: 'Selection' },
  { value: 'convergence',     label: 'Convergence' },
  { value: 'range_selection', label: 'Range Selection' },
  { value: 'review',          label: 'Review' },
];

// ============================================================================
// Subject kind — decision ontology (canon 27)
// ============================================================================

/**
 * Classifies a decision by SUBJECT — what the choice governs. Orthogonal to
 * DecisionClass, which classifies by form of judgment.
 */
export type DecisionSubjectKind =
  | 'compositional'
  | 'criterial'
  | 'valuational'
  | 'ordinal'
  | 'authoritative'
  | 'exceptional'
  | 'delegative'
  | 'revisional';

export const DECISION_SUBJECT_KIND_LABELS: Record<DecisionSubjectKind, string> = {
  compositional: 'Compositional',
  criterial:     'Criterial',
  valuational:   'Valuational',
  ordinal:       'Ordinal',
  authoritative: 'Authoritative',
  exceptional:   'Exceptional',
  delegative:    'Delegative',
  revisional:    'Revisional',
};

export const DECISION_SUBJECT_KIND_HINTS: Record<DecisionSubjectKind, string> = {
  compositional: 'What competes',
  criterial:     'How we judge',
  valuational:   'What it is worth',
  ordinal:       'What matters most',
  authoritative: 'What is ratified',
  exceptional:   'Sanctioned departures',
  delegative:    'Who may decide',
  revisional:    'Decisions about decisions',
};

/** Stripe / accent hex per subject kind (light-surface palette). */
export const DECISION_SUBJECT_KIND_COLORS: Record<DecisionSubjectKind, string> = {
  compositional: '#3B82F6', // blue-500
  criterial:     '#6366F1', // indigo-500
  valuational:   '#0D9488', // teal-600
  ordinal:       '#D97706', // amber-600
  authoritative: '#059669', // emerald-600
  exceptional:   '#DC2626', // red-600
  delegative:    '#8B5CF6', // violet-500
  revisional:    '#EA580C', // orange-600
};

/** Auto-decision source → subject kind, for rows recorded before migration 567. */
const SOURCE_TO_SUBJECT_KIND: Record<string, DecisionSubjectKind> = {
  session_published:      'ordinal',
  criteria_finalized:     'criterial',
  priority_list_approved: 'authoritative',
  proposal_accepted:      'authoritative',
};

/** Form-of-judgment fallback for rows with a class but no subject kind. */
const CLASS_TO_SUBJECT_KIND: Record<DecisionClass, DecisionSubjectKind> = {
  selection:       'compositional',
  range_selection: 'criterial',
  convergence:     'valuational',
  force_ranking:   'ordinal',
  review:          'revisional',
};

/**
 * Resolve a decision's subject kind. The explicit column wins; legacy rows
 * fall back to source, then decision class. Returns null when nothing can
 * be inferred (rendered as "Unclassified").
 */
export function resolveSubjectKind(d: Pick<Decision, 'subjectKind' | 'source' | 'decisionClass'>): DecisionSubjectKind | null {
  if (d.subjectKind) return d.subjectKind;
  if (d.source && SOURCE_TO_SUBJECT_KIND[d.source]) return SOURCE_TO_SUBJECT_KIND[d.source];
  if (d.decisionClass) return CLASS_TO_SUBJECT_KIND[d.decisionClass];
  return null;
}

// ============================================================================
// Core record
// ============================================================================

export interface Decision {
  id: string;
  workspaceId: string;

  title: string;
  body?: string | null;

  decisionKind: DecisionKind;

  /** Structural class from registry-decision-classes.yaml. */
  decisionClass?: DecisionClass | null;

  /** Event-type string that originated an auto decision (e.g. 'cycle_published'). */
  source?: string | null;

  /** ID of the originating audit_events / governance_events row. */
  sourceEventId?: string | null;

  /** Optional item this decision relates to. */
  itemId?: string | null;

  /** Optional session this decision relates to. */
  sessionId?: string | null;

  /** Optional cycle this decision relates to. */
  cycleId?: string | null;

  /** Optional item pool (focus list) this decision relates to. */
  itemPoolId?: string | null;

  decidedBy?: string | null;
  decidedAt: string;    // ISO string

  impact: DecisionImpact;
  status: DecisionStatus;
  tags: string[];

  /** Objects this decision relates to (multi-attach). */
  relatedObjects: DecisionRelatedObject[];

  // ── Rationale Infrastructure ─────────────────────────────────────────────
  /** What was true in the environment when this decision was made. */
  rationaleContext?: string | null;
  /** Why this made sense given that context. */
  rationaleReasoning?: string | null;
  /** What else was considered and why it was passed over. */
  rationaleAlternatives?: string | null;
  /** Signals that would cause this decision to be revisited. */
  reconsiderationConditions?: string | null;
  /** ISO date string: when this decision should be reviewed. */
  reviewByDate?: string | null;

  createdAt: string;
  updatedAt: string;

  /** Product module that created this decision (migration 490). Default: prioritization. */
  originModule?: OriginModule;

  /** Collaboration space this decision belongs to (migration 492). NULL = workspace-wide / org-level. */
  spaceId?: string | null;

  // ── Decision ontology (migration 567, canon 27) ──────────────────────────
  /** Subject taxonomy: what the choice governs. Orthogonal to decisionClass. */
  subjectKind?: DecisionSubjectKind | null;
  /** Sub-decision structure: the decision this one is a component of. */
  parentDecisionId?: string | null;
  /** Revision protocol: the prior decision this one replaces. */
  supersedesDecisionId?: string | null;
  /** Decision site: the venue where the decision was made. */
  site?: string | null;

  // Joined / derived fields (not persisted)
  decidedByName?: string | null;
  itemTitle?: string | null;
  /** Computed from rationale completeness and reviewByDate. Not stored in DB. */
  healthStatus?: DecisionHealthStatus;
}

// ============================================================================
// Create / update payloads
// ============================================================================

export interface CreateDecisionPayload {
  title: string;
  body?: string;
  decisionKind?: DecisionKind;
  decisionClass?: DecisionClass;
  source?: string;
  sourceEventId?: string;
  itemId?: string;
  sessionId?: string;
  cycleId?: string;
  itemPoolId?: string;
  decidedAt?: string;
  impact?: DecisionImpact;
  status?: DecisionStatus;
  tags?: string[];
  relatedObjects?: DecisionRelatedObject[];
  rationaleContext?: string;
  rationaleReasoning?: string;
  rationaleAlternatives?: string;
  reconsiderationConditions?: string;
  reviewByDate?: string;
  originModule?: OriginModule;
  subjectKind?: DecisionSubjectKind;
  parentDecisionId?: string;
  supersedesDecisionId?: string;
  site?: string;
}

export interface UpdateDecisionPayload {
  title?: string;
  body?: string;
  decisionClass?: DecisionClass | null;
  impact?: DecisionImpact;
  status?: DecisionStatus;
  tags?: string[];
  relatedObjects?: DecisionRelatedObject[];
  decidedAt?: string;
  rationaleContext?: string | null;
  rationaleReasoning?: string | null;
  rationaleAlternatives?: string | null;
  reconsiderationConditions?: string | null;
  reviewByDate?: string | null;
  subjectKind?: DecisionSubjectKind | null;
  parentDecisionId?: string | null;
  supersedesDecisionId?: string | null;
  site?: string | null;
}

// ============================================================================
// Provenance feed event — unified view across all tables
// ============================================================================

export type ProvenanceEventKind =
  | 'decision'
  | 'item_transition'
  | 'value_proposal'
  | 'value_acceptance'
  | 'value_rejection'
  | 'item_work'
  | 'session'
  | 'cycle'
  | 'governance'
  | 'ranking'
  | 'criteria'
  | 'lock'
  | 'lineage'
  | 'other';

// ============================================================================
// Object reference — the specific instance that was changed
// ============================================================================

export type ProvenanceObjectKind =
  | 'item'
  | 'session'
  | 'cycle'
  | 'pool'
  | 'track'
  | 'workflow'
  | 'unknown';

export const PROVENANCE_OBJECT_KIND_LABELS: Record<ProvenanceObjectKind, string> = {
  item:     'Item',
  session:  'Session',
  cycle:    'Cycle',
  pool:     'Item Pool',
  track:    'Track',
  workflow: 'Workflow',
  unknown:  'Object',
};

export interface ProvenanceObjectRef {
  kind: ProvenanceObjectKind;
  id: string;
  /** Name hint extracted from the event payload — may be absent; always prefer store lookup. */
  nameHint?: string | null;
  /** Pre-computed canonical href for navigation. */
  href: string;
}

export interface ProvenanceEvent {
  id: string;
  /** Discriminant for filter chips. */
  kind: ProvenanceEventKind;
  /** Raw event_type string from the source table. */
  eventType: string;
  /** Human-readable summary. */
  summary: string;
  occurredAt: string;    // ISO string
  actorName?: string | null;
  actorId?: string | null;
  /** The source table / object this came from. */
  source: 'decision' | 'audit_events' | 'governance_events' | 'planning_audit_events' | 'lineage_events';
  /**
   * The specific object instance that was acted upon.
   * Replaces the old itemId / itemTitle fields; use objectRef instead.
   */
  objectRef?: ProvenanceObjectRef | null;
  /** @deprecated use objectRef */
  itemId?: string | null;
  /** @deprecated use objectRef */
  itemTitle?: string | null;
  /** Optional extra payload for detail view. */
  payload?: Record<string, unknown> | null;
  /** For 'decision' kind: the full Decision record. */
  decision?: Decision | null;
}

// ============================================================================
// Helpers
// ============================================================================

export const PROVENANCE_KIND_LABELS: Record<ProvenanceEventKind, string> = {
  decision:         'Decision',
  item_transition:  'Item Transition',
  value_proposal:   'Value Proposal',
  value_acceptance: 'Value Accepted',
  value_rejection:  'Value Rejected',
  item_work:        'Item Work',
  session:          'Session',
  cycle:            'Cycle',
  governance:       'Governance',
  ranking:          'Ranking',
  criteria:         'Criteria',
  lock:             'Lock / Unlock',
  lineage:          'Lineage Event',
  other:            'Other',
};

export const PROVENANCE_KIND_COLORS: Record<ProvenanceEventKind, string> = {
  decision:         'bg-violet-100 text-violet-700',
  item_transition:  'bg-blue-100 text-blue-700',
  value_proposal:   'bg-sky-100 text-sky-700',
  value_acceptance: 'bg-emerald-100 text-emerald-700',
  value_rejection:  'bg-red-100 text-red-700',
  item_work:        'bg-amber-100 text-amber-700',
  session:          'bg-indigo-100 text-indigo-700',
  cycle:            'bg-cyan-100 text-cyan-700',
  governance:       'bg-orange-100 text-orange-700',
  ranking:          'bg-purple-100 text-purple-700',
  criteria:         'bg-teal-100 text-teal-700',
  lock:             'bg-slate-100 text-slate-700',
  lineage:          'bg-teal-100 text-teal-800',
  other:            'bg-gray-100 text-gray-600',
};

/** Map a raw event_type string to a ProvenanceEventKind. */
export function eventTypeToKind(eventType: string): ProvenanceEventKind {
  if (eventType === 'decision') return 'decision';

  const lower = eventType.toLowerCase();

  // Lineage transformations (from lineage_events table) — checked first since
  // 'merge' and 'split' would otherwise fall into 'item_work'.
  if (lower === 'split' || lower === 'merge' || lower === 'supersede' ||
      lower === 'retire' || lower === 'fork') return 'lineage';

  if (lower.includes('transition') || lower.includes('promote') || lower.includes('demote') ||
      lower.includes('item_promoted') || lower.includes('item_demoted') ||
      lower === 'item_transition') return 'item_transition';

  if (lower.includes('proposal') || lower === 'proposal_submitted') return 'value_proposal';
  if (lower.includes('acceptance') || lower === 'value_accepted') return 'value_acceptance';
  if (lower.includes('rejection') || lower === 'value_rejected') return 'value_rejection';

  if (lower.includes('shap') || lower.includes('altitude') || lower.includes('dedupe') ||
      lower.includes('split') || lower.includes('merged') || lower.includes('duplicate') ||
      lower.includes('item_title') || lower.includes('item_description') ||
      lower.includes('item_category') || lower.includes('item_deleted')) return 'item_work';

  if (lower.includes('session')) return 'session';

  if (lower.includes('cycle') || lower.includes('publish') || lower.includes('cycle_published')) return 'cycle';

  if (lower.includes('rank') || lower.includes('cutline') || lower.includes('aggregat')) return 'ranking';

  if (lower.includes('criteria') || lower.includes('weight') || lower.includes('attribute_owner')) return 'criteria';

  if (lower.includes('lock') || lower.includes('unlock')) return 'lock';

  if (lower.includes('role') || lower.includes('decision_right') || lower.includes('workflow') ||
      lower.includes('permission') || lower.includes('policy') || lower.includes('governance') ||
      lower.includes('official_result')) return 'governance';

  return 'other';
}
